import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {api} from "./api";

type Job = {id: string; title: string};
export function startBrowserCad(id: string, title: string) {
  window.dispatchEvent(new CustomEvent("cad-browser-request", {detail: {id, title}}));
}

export function BrowserCadConversion() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [retry, setRetry] = useState(0);
  const [message, setMessage] = useState("");
  const [phase, setPhase] = useState("starting");
  const [source, setSource] = useState("");
  const frame = useRef<HTMLIFrameElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const job = jobs[0];
  useEffect(() => {
    const receive = (event: Event) => {
      const next = (event as CustomEvent<Job>).detail;
      setJobs(current => current.some(item => item.id === next.id) ? current : [...current, next]);
    };
    window.addEventListener("cad-browser-request", receive);
    return () => window.removeEventListener("cad-browser-request", receive);
  }, []);
  useEffect(() => {
    if (!job) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        const buttons = Array.from(document.querySelectorAll<HTMLElement>(".cad-browser-dialog button:not(:disabled), .cad-browser-dialog a[href]"));
        const index = buttons.indexOf(document.activeElement as HTMLElement);
        event.preventDefault(); event.stopImmediatePropagation();
        buttons[(index + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length]?.focus();
      }
      if (event.key === "Escape") { event.preventDefault(); event.stopImmediatePropagation(); }
    };
    document.addEventListener("keydown", keydown, true);
    return () => { document.body.style.overflow = overflow; document.removeEventListener("keydown", keydown, true); previousFocus?.focus(); };
  }, [job]);
  useEffect(() => {
    if (!job) return;
    let active = true;
    let ticket: Awaited<ReturnType<typeof api.browserCadTicket>> | undefined;
    setPhase("starting"); setMessage("原图已保存，正在准备转换组件"); setSource("");
    const fail = (text: string) => {if(active){window.removeEventListener("beforeunload",beforeUnload);setMessage(text);setPhase("error");setSource("");}};
    const timeout = window.setTimeout(() => fail("生成超时，原图已保存。可以稍后重试或下载 DWG。"), 180_000);
    const receive = (event: MessageEvent) => {
      if (!active || event.origin !== location.origin || event.source !== frame.current?.contentWindow || event.data?.type !== "cad-browser") return;
      if (event.data.status === "ready" && ticket) {
        const cookie = document.cookie.split("; ").find(value => value.startsWith("effect_archive_csrf="));
        frame.current?.contentWindow?.postMessage({type:"cad-browser-start",fileId:job.id,...ticket,csrf:cookie ? decodeURIComponent(cookie.split("=")[1]) : ""},location.origin);
        setPhase("running");
      } else if (event.data.status === "progress") setMessage(event.data.message);
      else if (event.data.status === "error") {clearTimeout(timeout);fail(event.data.message);}
      else if (event.data.status === "done") {
        window.removeEventListener("beforeunload",beforeUnload);
        clearTimeout(timeout);setPhase("done");setMessage(event.data.message);setSource("");
        window.dispatchEvent(new CustomEvent("cad-browser-finished",{detail:job.id}));
      }
    };
    const beforeUnload = (event: BeforeUnloadEvent) => {event.preventDefault();event.returnValue="";};
    window.addEventListener("message", receive);
    window.addEventListener("beforeunload", beforeUnload);
    void api.browserCadTicket(job.id).then(value => {if(active){ticket=value;setSource(value.runner_url);}}).catch(error=>fail(error.message));
    return () => {active=false;clearTimeout(timeout);window.removeEventListener("message", receive);window.removeEventListener("beforeunload",beforeUnload);};
  }, [job, retry]);
  if (!job) return null;
  const busy = phase === "starting" || phase === "running";
  return createPortal(<div className="cad-browser-overlay"><section className="cad-browser-dialog" role="dialog" aria-modal="true" aria-labelledby="cad-browser-title">
    <span className="cad-browser-eyebrow">CAD 预览</span><h2 id="cad-browser-title">{busy ? "正在生成预览" : phase === "done" ? "预览已就绪" : "暂未生成预览"}</h2>
    <p className="cad-browser-name">{job.title}</p><p role="status">{message}</p>
    <p className="cad-browser-note">{busy ? "使用当前电脑生成，请保持本页打开。原始 DWG 已保存，关闭本窗口不会删除原图。" : phase === "done" ? "预览已存入网站，可以关闭页面或电脑。员工看图不依赖这台电脑开机。" : "原始 DWG 已保存，仍可下载。建议使用电脑重新生成预览。"}</p>
    {jobs.length > 1 && <p>还有 {jobs.length - 1} 份等待处理</p>}
    <div className="cad-browser-actions">{phase === "error" && <button type="button" className="primary-action" onClick={()=>setRetry(value=>value+1)}>重新生成</button>}<button ref={closeButton} type="button" className="secondary-action" onClick={()=>{setSource("");setJobs(current=>current.slice(1));}}>{busy ? "停止生成" : jobs.length>1 ? "处理下一份" : "完成并返回"}</button></div>
    <p className="cad-browser-note"><a href="/api/admin/cad-browser/legal/NOTICES.html" target="_blank" rel="noopener noreferrer">开源组件与源码</a></p>
    {source && <iframe ref={frame} className="cad-browser-engine" title="CAD 转换进程" src={source} sandbox="allow-scripts allow-same-origin" tabIndex={-1} aria-hidden="true" />}
  </section></div>,document.body);
}
